import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import {
  TrendingUp, Truck, Users, Car, Fuel, DollarSign, FileText, ArrowUpRight, ArrowDownRight, Package,
  AlertTriangle, Clock, CheckCircle, Shield
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useNavigate } from "react-router-dom";

const PIE_COLORS = ["#eab308", "#3b82f6", "#f97316", "#10b981", "#ef4444"];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [complianceAlerts, setComplianceAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/dashboard/stats`),
      axios.get(`${API}/compliance/alerts`)
    ]).then(([statsRes, alertsRes]) => {
      setStats(statsRes.data);
      setComplianceAlerts(alertsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) return null;

  const kpis = [
    { label: "Total Revenue", value: `$${(stats.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    { label: "Pending Revenue", value: `$${(stats.pending_revenue || 0).toLocaleString()}`, icon: TrendingUp, color: "text-yellow-500", bg: "bg-yellow-500/10" },
    { label: "Active Jobs", value: stats.active_jobs, icon: Package, color: "text-orange-500", bg: "bg-orange-500/10" },
    { label: "Total Jobs", value: stats.total_jobs, icon: Truck, color: "text-blue-500", bg: "bg-blue-500/10" },
    { label: "Drivers", value: `${stats.available_drivers}/${stats.total_drivers}`, icon: Users, color: "text-violet-500", bg: "bg-violet-500/10", sub: "available" },
    { label: "Vehicles", value: `${stats.active_vehicles}/${stats.total_vehicles}`, icon: Car, color: "text-cyan-500", bg: "bg-cyan-500/10", sub: "active" },
    { label: "Fuel Costs", value: `$${(stats.total_fuel_cost || 0).toLocaleString()}`, icon: Fuel, color: "text-amber-500", bg: "bg-amber-500/10" },
    { label: "Expenses", value: `$${(stats.total_expenses || 0).toLocaleString()}`, icon: FileText, color: "text-red-500", bg: "bg-red-500/10" },
  ];

  const jobStatusData = Object.entries(stats.jobs_by_status || {}).map(([key, val]) => ({ name: key, value: val })).filter(d => d.value > 0);

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const revenueData = (stats.monthly_revenue || []).map(d => ({
    month: monthNames[parseInt(d.month.split("-")[1]) - 1] || d.month,
    revenue: d.revenue
  }));

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Overview of your logistics operations" />

      <div className="p-6 space-y-6">
        {/* Compliance Alerts Banner */}
        {complianceAlerts && (complianceAlerts.total_expired > 0 || complianceAlerts.total_expiring > 0) && (
          <div className="border border-zinc-800 bg-[#18181B] rounded-sm overflow-hidden" data-testid="compliance-alerts-section">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                <h3 className="font-heading text-sm font-bold text-white tracking-tight">Compliance Alerts</h3>
                {complianceAlerts.total_expired > 0 && (
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] font-medium rounded-sm">
                    {complianceAlerts.total_expired} EXPIRED
                  </span>
                )}
                {complianceAlerts.total_expiring > 0 && (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-medium rounded-sm">
                    {complianceAlerts.total_expiring} EXPIRING SOON
                  </span>
                )}
              </div>
              <span className="text-[10px] text-zinc-500">7-day alert window</span>
            </div>
            <div className="max-h-[200px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-zinc-900/90 backdrop-blur">
                  <tr className="border-b border-zinc-800/50">
                    <th className="text-left px-4 py-2 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Type</th>
                    <th className="text-left px-4 py-2 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Name</th>
                    <th className="text-left px-4 py-2 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Document</th>
                    <th className="text-left px-4 py-2 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Expiry Date</th>
                    <th className="text-left px-4 py-2 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {complianceAlerts.alerts.map((alert, idx) => (
                    <tr 
                      key={idx} 
                      className="border-b border-zinc-800/30 hover:bg-zinc-800/20 cursor-pointer"
                      onClick={() => navigate(alert.type === "vehicle" ? "/app/vehicles" : "/app/drivers")}
                      data-testid={`compliance-alert-${idx}`}
                    >
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-medium ${
                          alert.type === "vehicle" ? "bg-cyan-500/10 text-cyan-400" : "bg-violet-500/10 text-violet-400"
                        }`}>
                          {alert.type === "vehicle" ? <Car className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                          {alert.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-zinc-200 text-xs">{alert.entity_name}</td>
                      <td className="px-4 py-2 text-zinc-400 text-xs">{alert.document}</td>
                      <td className="px-4 py-2 font-mono text-xs text-zinc-300">{alert.expiry_date}</td>
                      <td className="px-4 py-2">
                        {alert.status === "expired" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 rounded-sm">
                            <AlertTriangle className="w-3 h-3 text-red-500" />
                            <span className="text-[10px] font-medium text-red-400">
                              Expired {Math.abs(alert.days_until)}d ago
                            </span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-sm">
                            <Clock className="w-3 h-3 text-amber-500" />
                            <span className="text-[10px] font-medium text-amber-400">
                              {alert.days_until}d left
                            </span>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children" data-testid="dashboard-kpis">
          {kpis.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <div
                key={kpi.label}
                className="border border-zinc-800 bg-[#18181B] rounded-sm p-4 animate-fade-in"
                data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] text-zinc-500 uppercase tracking-wider font-medium">{kpi.label}</span>
                  <div className={`w-7 h-7 rounded-sm ${kpi.bg} flex items-center justify-center`}>
                    <Icon className={`w-3.5 h-3.5 ${kpi.color}`} />
                  </div>
                </div>
                <p className="font-heading text-2xl font-bold text-white tracking-tight">{kpi.value}</p>
                {kpi.sub && <p className="text-[10px] text-zinc-500 mt-0.5">{kpi.sub}</p>}
              </div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Revenue Chart */}
          <div className="lg:col-span-2 border border-zinc-800 bg-[#18181B] rounded-sm p-4" data-testid="revenue-chart">
            <h3 className="font-heading text-sm font-bold text-white mb-4 tracking-tight">Monthly Revenue</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={revenueData}>
                <XAxis dataKey="month" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: '#27272a', border: '1px solid #3f3f46', borderRadius: '2px', fontSize: 12 }}
                  labelStyle={{ color: '#a1a1aa' }}
                  itemStyle={{ color: '#f97316' }}
                />
                <Bar dataKey="revenue" fill="#f97316" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Jobs by Status Pie */}
          <div className="border border-zinc-800 bg-[#18181B] rounded-sm p-4" data-testid="jobs-status-chart">
            <h3 className="font-heading text-sm font-bold text-white mb-4 tracking-tight">Jobs by Status</h3>
            {jobStatusData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={jobStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
                      {jobStatusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#27272a', border: '1px solid #3f3f46', borderRadius: '2px', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-3 mt-2">
                  {jobStatusData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-[10px] text-zinc-400 capitalize">{d.name.replace("_", " ")}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-zinc-500 text-sm text-center py-10">No jobs yet</p>
            )}
          </div>
        </div>

        {/* Recent Jobs */}
        <div className="border border-zinc-800 bg-[#18181B] rounded-sm" data-testid="recent-jobs-table">
          <div className="p-4 border-b border-zinc-800">
            <h3 className="font-heading text-sm font-bold text-white tracking-tight">Recent Jobs</h3>
          </div>
          {stats.recent_jobs?.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800/50">
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Title</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Driver</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Pickup</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Delivery</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_jobs.map((job) => (
                  <tr key={job.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/20">
                    <td className="px-4 py-2.5 text-zinc-200 font-medium">{job.title}</td>
                    <td className="px-4 py-2.5 text-zinc-400">{job.driver_name || "—"}</td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs">{job.pickup_location || "—"}</td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs">{job.delivery_location || "—"}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={job.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-zinc-500 text-sm text-center py-8">No jobs created yet. Start by adding a new job.</p>
          )}
        </div>
      </div>
    </div>
  );
}
